import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from 'react-i18next';
import { Outlet } from "react-router-dom";


export const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  if (isLoading) {
    // Optional: Show a loading spinner while checking auth state
    return <div>{t('loadingAuthentication')}</div>; 
  }

  if (!isAuthenticated) {
    // For an external auth provider, direct navigation to /login is not how auth is initiated.
    // Instead, UI elements (like a login button) should call authService.signIn().
    // If a protected route is accessed without auth, we could redirect to a generic
    // landing page, or the App component could show a login button.
    // For now, redirecting to '/' might cause a loop if '/' is protected.
    // A better approach might be to have a specific public landing page or
    // rely on UI elements to prompt login.
    // However, to prevent breaking the existing redirect logic entirely without a designated
    // public landing page, we'll keep the redirect but it should ideally point to a page
    // that can initiate signIn() or be a public page.
    // For this refactor, we'll assume the user is guided to sign in via UI elements,
    // and this redirect might be less frequently hit directly.
    // If direct access to a protected path happens, it should ideally prompt sign-in.
    // A simple <Navigate to="/" /> might be okay if the root path "/" handles unauthenticated users gracefully
    // by showing a login prompt or being a public page.
    // For now, let's assume the external sign-in button is the primary way to login.
    // If direct nav to protected route, redirecting to root which might show login button or be public.
    // Or, if we had a specific "please login" page, that would be better.
    // Given the new flow, this redirect to /login is problematic.
    // Let's remove the explicit redirect to /login from here for now.
    // The `isAuthenticated` check in `AuthContext` and `useUserSession` will gate content.
    // UI should provide a login mechanism (e.g. a button calling `signIn()`).
    // If an unauthenticated user hits a protected route, they simply won't see the content.
    // The `AuthContext` `isLoading` and `!isAuthenticated` state should be used by UI
    // to show appropriate "Login" buttons or messages.
    // So, if not authenticated, Outlet won't render protected content.
    // This means ProtectedRoute might just return <Outlet /> and rely on parent components
    // or the page components themselves to handle the !isAuthenticated case by showing login prompts.
    // Or, it could return a "Please login" component.
    // For now, let's keep it simple: if not authenticated, it just doesn't render the protected content.
    // The UI (e.g. Header) should provide login options.
    return null; // Or a specific "Please Login" component/message
  }
  return <Outlet />; // Render child routes if authenticated
};